import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { emailChangeService, EmailChangePublicMeta } from "@/services/emailChange";
import { ensureCsrfToken } from "@/utils/csrf";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ConfirmEmailChange: React.FC = () => {
  const { token: rawToken } = useParams<{ token: string }>();
  const token = rawToken ? decodeURIComponent(rawToken) : "";
  const navigate = useNavigate();

  const [meta, setMeta] = useState<EmailChangePublicMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("Invalid email confirmation link.");
      setLoading(false);
      return;
    }
    emailChangeService
      .getByToken(token)
      .then((data) => setMeta(data))
      .catch((e) =>
        setLoadError(e instanceof Error ? e.message : "This confirmation link is invalid.")
      )
      .finally(() => setLoading(false));
  }, [token]);

  const onConfirm = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await ensureCsrfToken();
      const res = await emailChangeService.confirm(token);
      setSuccess(res.detail || "Email updated successfully.");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Could not confirm email change.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderShell = (children: React.ReactNode) => (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md space-y-6 p-8">{children}</Card>
    </div>
  );

  if (loading) {
    return renderShell(
      <div className="flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError || !meta) {
    return renderShell(
      <>
        <div className="text-center">
          <h1 className="text-xl font-semibold tracking-tight">Confirm email change</h1>
        </div>
        <Alert variant="destructive" className="text-center">
          <AlertDescription>
            {loadError || "This confirmation link is no longer valid."}
          </AlertDescription>
        </Alert>
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate("/login")}>
            Go to login
          </Button>
        </div>
      </>
    );
  }

  if (success) {
    return renderShell(
      <>
        <div className="space-y-2 text-center">
          <h1 className="text-xl font-semibold tracking-tight">Email updated</h1>
          <p className="text-sm text-muted-foreground">
            Your login email is now <span className="font-medium text-foreground">{meta.new_email}</span>
            . Sign in with this address going forward.
          </p>
        </div>
        <Alert className="border-green-600/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
        <div className="flex justify-center">
          <Button onClick={() => navigate("/login")}>Go to login</Button>
        </div>
      </>
    );
  }

  return renderShell(
    <>
      <div className="space-y-2 text-center">
        <h1 className="text-xl font-semibold tracking-tight">Confirm email change</h1>
        <p className="text-sm text-muted-foreground">
          Change login email from <span className="font-medium text-foreground">{meta.current_email}</span>{" "}
          to <span className="font-medium text-foreground">{meta.new_email}</span>.
        </p>
      </div>

      {submitError && (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <Button className="w-full" onClick={onConfirm} disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm new email"}
      </Button>
    </>
  );
};

export default ConfirmEmailChange;
